import gql from 'graphql-tag';
import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import InfoIcon from '@watershed/icons/components/Info';
import Callout from '@watershed/shared-frontend/components/Callout';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import Button from '@watershed/ui-core/components/Button';
import CopyButton from '@watershed/ui-core/components/CopyButton';
import { TextFieldNonFormik } from '@watershed/ui-core/components/Form/TextField';
import ProgressButton from '@watershed/ui-core/components/ProgressButton';
import StepDialog, {
  StepDialogStep,
} from '@watershed/ui-core/components/StepDialog';

import { useRegenerateOauthClientSecretDialogMutation } from '../../../generated/urql';

gql`
  mutation RegenerateOauthClientSecretDialog(
    $input: RegenerateOauthClientSecretInput!
  ) @withOwner(owner: EnterpriseFoundations) {
    regenerateOauthClientSecret(input: $input) {
      client {
        id
        secretSuffix
        activeApiKeys {
          id
          createdAt
          expiresAt
        }
      }
      secret
    }
  }
`;

type ClientIdAndSecret = {
  clientId: string;
  secret: string;
};

export default function RegenerateOAuthClientSecretDialog({
  open,
  onClose,
  clientId,
  clientName,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const onNext = () => setCurrentStep((curr) => curr + 1);
  const [, executeRegenerate] = useRegenerateOauthClientSecretDialogMutation();
  const [clientIdAndSecret, setClientIdAndSecret] =
    useState<ClientIdAndSecret | null>(null);
  const resetClientIdAndSecret = () => {
    setClientIdAndSecret(null);
  };
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      resetClientIdAndSecret();
      setCurrentStep(1);
    }, 500);
  };

  const onRegenerate = async () => {
    setSubmitting(true);
    const result = await executeRegenerate({
      input: { id: clientId },
    });
    if (result.error) {
      enqueueSnackbar('Error regenerating OAuth client secret.', {
        variant: 'error',
      });
      setSubmitting(false);
      return;
    }

    const client = result.data?.regenerateOauthClientSecret?.client;
    const secret = result.data?.regenerateOauthClientSecret?.secret;

    if (!client || !secret) {
      enqueueSnackbar('Error regenerating OAuth client secret.', {
        variant: 'error',
      });
      setSubmitting(false);
      return;
    }

    setClientIdAndSecret({
      clientId: client.id,
      secret,
    });
    setSubmitting(false);
    onNext();
  };

  const steps: Array<StepDialogStep> = [
    {
      children: (
        <Stack gap={3}>
          <Typography variant="body1">
            <Trans context="Regenerate OAuth client secret confirmation">
              Are you sure you want to regenerate the client secret for "
              {clientName}"?
            </Trans>
          </Typography>
          <Callout
            variant="warning"
            IconComponent={InfoIcon}
            description={
              <Trans context="Regenerate OAuth client secret warning">
                This action will generate a new client secret and make the
                current one invalid. Any applications using the current secret
                will need to be updated with the new secret.
              </Trans>
            }
          />
        </Stack>
      ),
      dialogProps: {
        header: {
          title: t({
            message: 'Regenerate client secret',
            context: 'dialog title',
          }),
        },
        actions: (
          <ProgressButton
            onClick={onRegenerate}
            isInProgress={submitting}
            color="primary"
          >
            <Trans context="Button copy">Regenerate secret</Trans>
          </ProgressButton>
        ),
      },
    },
    {
      children: (
        <Stack gap={2}>
          <Callout
            variant="warning"
            IconComponent={InfoIcon}
            description={
              <Trans context="Regenerate OAuth client secret step dialog">
                Make sure you save your new client secret, as you won't be able
                to view it again. Keep it secure; anyone with your credentials
                can make requests on your behalf. If you lose it, you'll need to
                regenerate a new one.
              </Trans>
            }
          />
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <TextFieldNonFormik
              id="clientId"
              sx={{
                flexGrow: 1,
              }}
              label={
                <Typography variant="h4">
                  <Trans context="form field label">Client ID</Trans>
                </Typography>
              }
              value={clientIdAndSecret?.clientId}
              // @ts-expect-error - InputProps is valid for TextFieldNonFormik
              InputProps={{
                readOnly: true,
              }}
            />
            <CopyButton
              text={clientIdAndSecret?.clientId}
              iconOnly
              color="primary"
              size="large"
            />
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <TextFieldNonFormik
              id="secret"
              sx={{
                flexGrow: 1,
              }}
              label={
                <Typography variant="h4">
                  <Trans context="form field label">Client secret</Trans>
                </Typography>
              }
              value={clientIdAndSecret?.secret}
              // @ts-expect-error - InputProps is valid for TextFieldNonFormik
              InputProps={{
                readOnly: true,
              }}
            />
            <CopyButton
              text={clientIdAndSecret?.secret}
              iconOnly
              color="primary"
              size="large"
            />
          </Stack>
        </Stack>
      ),
      dialogProps: {
        header: {
          title: t({
            message: 'Regenerate client secret',
            context: 'dialog title',
          }),
          hideClose: true,
        },
        actions: (
          <Button onClick={handleClose} color="primary">
            <Trans context="Button copy for finishing the oauth flow">
              Finish
            </Trans>
          </Button>
        ),
      },
    },
  ];

  return (
    <StepDialog
      stepCount={steps.length}
      currentStep={currentStep}
      steps={steps}
      onStepChange={setCurrentStep}
      defaultDialogProps={{
        open,
        onClose: handleClose,
      }}
      // we disable step change because going backwards is not supported
      disableStepChange
    />
  );
}
